import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';

interface CTASectionProps {
  title: string;
  subtitle: string;
  primaryButton: {
    text: string;
    href: string;
    variant?: 'contained' | 'outlined' | 'text';
  };
  secondaryButton?: {
    text: string;
    href: string;
    variant?: 'contained' | 'outlined' | 'text';
  };
  backgroundColor?: string;
  textColor?: string;
}

export const CTASection: React.FC<CTASectionProps> = ({
  title,
  subtitle,
  primaryButton,
  secondaryButton,
  backgroundColor,
  textColor = 'white',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: backgroundColor || theme.palette.primary.main,
        color: textColor,
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          component="h2"
          sx={{
            fontWeight: 'bold',
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{
            mb: 4,
            opacity: 0.9,
            lineHeight: 1.4,
          }}
        >
          {subtitle}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant={primaryButton.variant || 'contained'}
            size="large"
            href={primaryButton.href}
            endIcon={<ArrowForwardIcon />}
            sx={{
              minWidth: { xs: '100%', sm: 'auto' },
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              backgroundColor: primaryButton.variant === 'outlined' ? 'transparent' : undefined,
              color: primaryButton.variant === 'outlined' ? textColor : undefined,
              borderColor: primaryButton.variant === 'outlined' ? textColor : undefined,
              '&:hover': {
                backgroundColor: primaryButton.variant === 'outlined' ? 'rgba(255,255,255,0.1)' : undefined,
                borderColor: primaryButton.variant === 'outlined' ? textColor : undefined,
              },
            }}
          >
            {primaryButton.text}
          </Button>
          {secondaryButton && (
            <Button
              variant={secondaryButton.variant || 'outlined'}
              size="large"
              href={secondaryButton.href}
              sx={{
                minWidth: { xs: '100%', sm: 'auto' },
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: secondaryButton.variant === 'contained' ? 'transparent' : undefined,
                color: secondaryButton.variant === 'contained' ? textColor : undefined,
                borderColor: secondaryButton.variant === 'contained' ? textColor : undefined,
                '&:hover': {
                  backgroundColor: secondaryButton.variant === 'contained' ? 'rgba(255,255,255,0.1)' : undefined,
                  borderColor: secondaryButton.variant === 'contained' ? textColor : undefined,
                },
              }}
            >
              {secondaryButton.text}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};
